import { useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or get session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('arrancaya_session');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem('arrancaya_session', sessionId);
  }
  return sessionId;
};

// Detect traffic source from URL params and referrer
const detectSource = () => {
  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer;
  
  // Check for Google Ads / paid campaigns
  if (params.get('gclid') || params.get('utm_medium') === 'cpc' || params.get('utm_medium') === 'ppc') {
    return 'sea';
  }
  
  // Check for organic search
  if (referrer) {
    const searchEngines = ['google.', 'bing.', 'yahoo.', 'duckduckgo.', 'ecosia.'];
    if (searchEngines.some(engine => referrer.includes(engine))) {
      // If no gclid, it's organic
      if (!params.get('gclid')) {
        return 'seo';
      }
    }
    return 'referral';
  }
  
  // Direct traffic
  return 'direct';
};

// Main tracking function
const trackEvent = async (eventType, page, extraData = {}) => {
  try {
    const params = new URLSearchParams(window.location.search);
    
    const payload = {
      event_type: eventType,
      page: page || window.location.pathname,
      source: detectSource(),
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      session_id: getSessionId(),
      extra_data: extraData
    };

    await fetch(`${API_URL}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  } catch (error) {
    console.warn('Analytics tracking failed:', error);
  }
};

// Hook for page view tracking
export const usePageTracking = (pageName) => {
  useEffect(() => {
    trackEvent('page_view', pageName || window.location.pathname);
  }, [pageName]);
};

// Hook for tracking actions
export const useAnalytics = () => {
  const trackPageView = useCallback((page) => {
    trackEvent('page_view', page);
  }, []);

  const trackWhatsAppClick = useCallback((page, extraData = {}) => {
    trackEvent('whatsapp_click', page, extraData);
  }, []);

  const trackPhoneClick = useCallback((page, extraData = {}) => {
    trackEvent('phone_click', page, extraData);
  }, []);

  const trackFormOpen = useCallback((page, formType = '') => {
    trackEvent('form_open', page, { form_type: formType });
  }, []);

  const trackFormSubmit = useCallback((page, formData = {}) => {
    trackEvent('form_submit', page, formData);
  }, []);

  const trackBlogView = useCallback((articleSlug) => {
    trackEvent('page_view', `/blog/${articleSlug}`, { content_type: 'blog' });
  }, []);

  return {
    trackPageView,
    trackWhatsAppClick,
    trackPhoneClick,
    trackFormOpen,
    trackFormSubmit,
    trackBlogView
  };
};

export default useAnalytics;
